import { dispatchTool, listTools } from './dispatcher';
import { notifyAgentAbort } from './abort';
import { useLayerRegistry } from '@/map/layers/registry';
import { useSelectionStore } from '@/state/selectionStore';
import { getMap, getMapView, useMapStore } from '@/map/mapStore';
import { translateAttributes } from '@/agent/tools/shared/layers';
import {
  summarizeToolInput,
  useAgentActivityStore,
} from '@/state/agentActivityStore';

let bridgeInstalled = false;

export function startDispatcherBridge(): () => void {
  if (bridgeInstalled) return () => {};
  bridgeInstalled = true;
  const offToolCall = window.api.onToolCall(async (call) => {
    const activity = useAgentActivityStore.getState();
    activity.startActivity({
      id: call.id,
      tool: call.name,
      inputSummary: summarizeToolInput(call.input),
    });
    const outcome = await dispatchTool(call.name, call.input);
    activity.finishActivity(
      call.id,
      outcome.ok,
      outcome.ok ? undefined : outcome.error,
    );
    const payload = outcome.ok ? outcome.result : { error: outcome.error };
    window.api.toolResult({
      id: call.id,
      ok: outcome.ok,
      result: payload,
    });
  });
  const offAborted = window.api.onChatAborted(() => {
    useAgentActivityStore.getState().clearPending();
    notifyAgentAbort();
  });
  return () => {
    offToolCall();
    offAborted();
    bridgeInstalled = false;
  };
}

export function buildToolDefs() {
  return listTools().map((t) => ({
    name: t.name,
    description: t.description,
    input_schema: t.input_schema as Record<string, unknown>,
  }));
}

const SELECTION_ATTRS_LIMIT = 8;

export function buildLayerIndexPayload() {
  const map = getMap();
  return useLayerRegistry.getState().records.map((r) => {
    const layer = map?.layers.find((l) => l.id === r.id);
    return {
      id: r.id,
      geometry_type: r.probe?.service.geometry_type,
      flags: Array.from(r.flags),
      has_notes: !!r.notes,
      status: r.status,
      is_visible: layer ? layer.visible !== false : undefined,
      display_name: r.probe?.service.display_field ?? undefined,
    };
  });
}

export function buildSelectionPayload() {
  const sel = useSelectionStore.getState().current;
  if (!sel) return null;
  const probe = useLayerRegistry.getState().records.find((r) => r.id === sel.layer_id)?.probe;
  const translated = translateAttributes(sel.attributes ?? {}, probe);
  const attributes = pickTopAttributes(translated, probe?.service.display_field);
  return {
    layer_id: sel.layer_id,
    objectid: sel.objectid,
    display: sel.display,
    centroid: sel.centroid,
    attributes,
  };
}

export function buildViewStatePayload() {
  const view = getMapView();
  const map = getMap();
  if (!view) return null;
  const records = useLayerRegistry.getState().records;
  const visibleIds: string[] = [];
  if (map) {
    for (const r of records) {
      const layer = map.layers.find((l) => l.id === r.id);
      if (layer && layer.visible !== false) visibleIds.push(r.id);
    }
  }
  const ext = view.extent;
  return {
    basemap_id: useMapStore.getState().basemapId,
    scale: typeof view.scale === 'number' ? view.scale : undefined,
    zoom: typeof view.zoom === 'number' ? view.zoom : undefined,
    extent: ext
      ? {
          xmin: ext.xmin,
          ymin: ext.ymin,
          xmax: ext.xmax,
          ymax: ext.ymax,
          wkid: ext.spatialReference?.wkid ?? undefined,
        }
      : undefined,
    visible_layer_ids: visibleIds,
  };
}

function pickTopAttributes(
  attrs: Record<string, unknown>,
  displayField: string | undefined,
): Record<string, unknown> {
  const entries = Object.entries(attrs).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return {};
  const ordered: [string, unknown][] = [];
  if (displayField) {
    const display = entries.find(([k]) => k === displayField);
    if (display) ordered.push(display);
  }
  for (const e of entries) {
    if (ordered.find(([k]) => k === e[0])) continue;
    ordered.push(e);
    if (ordered.length >= SELECTION_ATTRS_LIMIT) break;
  }
  return Object.fromEntries(ordered);
}
